import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, Grid, IconButton, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerContainer } from '../../../components/Drawer/DrawerContainer';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { enqueueSnackbar } from 'notistack';
import { ACTION_TYPES, ActionType } from '../consts/avisoObra.types';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import useAvisoObraActions from '../hooks/useAvisoObraActions';
import useSuspendAvisoObraForm, {
    SuspendAvisoObraFormData
} from '../hooks/useSuspendAvisoObraForm';
import CustomField from '../../../components/CustomFormsComponents/CustomField';
import { downloadFromBase64, getDate, handleErrors } from '../../../utils/common';
import useExtendAvisoObraForm, { ExtendAvisoObraFormData } from '../hooks/useExtendAvisoObraForm';
import {
    DrawerActionInfoPaper,
    DrawerActionInfoPaperItem
} from './DrawerInfo/DrawerActionInfoPaper';
import useRestartAvisoObraForm, {
    RestartAvisoObraFormData
} from '../hooks/useRestartAvisoObraForm';
import useDeregisterAvisoObraForm, {
    DeregisterAvisoObraFormData
} from '../hooks/useDeregisterAvisoObraForm';
import { useGetTiposObraQuery } from '../../../services/avisoObraApi';
import useUploadProgramaForm from '../hooks/useUploadProgramaForm';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { EstadoAvisoObraEnum } from '../consts/avisoObraStatesTypes';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { selectCurrentUser } from '../../../features/auth/authSlice';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';

interface DrawerAvisoObraProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    actionType: ActionType | null;
    selectedObra: any;
    onSuccess?: () => void;
}

const DrawerAvisoObra: React.FC<DrawerAvisoObraProps> = ({
    open,
    setOpen,
    actionType,
    selectedObra,
    onSuccess
}) => {
    const { t } = useTranslation('avisoObra');
    const dispatch = useAppDispatch();
    const { data: tiposObraData } = useGetTiposObraQuery();
    const user: any = useAppSelector(selectCurrentUser);

    const {
        suspendAvisoObra,
        extendAvisoObra,
        restartAvisoObra,
        deregisterAvisoObra,
        updateAvisoObra,
        isSuspendingAvisoObra,
        isExtendingAvisoObra,
        isRestartingAvisoObra,
        isDeregisteringAvisoObra,
        isUpdatingPrograma,
        generateConstanciaPdf,
        isGeneratingConstanciaPdf
    } = useAvisoObraActions();

    const {
        formData: suspendAvisoObraFormData,
        fieldsConfig: suspendAvisoObraFieldsConfig,
        handleChange: suspendAvisoObraHandleChange,
        errors: suspendAvisoObraErrors,
        validateForm: validateSuspendAvisoObraForm,
        resetForm: resetSuspendAvisoObraForm
    } = useSuspendAvisoObraForm(selectedObra);
    const {
        formData: extendAvisoObraFormData,
        fieldsConfig: extendAvisoObraFieldsConfig,
        handleChange: extendAvisoObraHandleChange,
        errors: extendAvisoObraErrors,
        validateForm: validateExtendAvisoObraForm,
        resetForm: resetExtendAvisoObraForm
    } = useExtendAvisoObraForm(selectedObra);

    const {
        formData: restartAvisoObraFormData,
        fieldsConfig: restartAvisoObraFieldsConfig,
        handleChange: restartAvisoObraHandleChange,
        errors: restartAvisoObraErrors,
        validateForm: validateRestartAvisoObraForm,
        resetForm: resetRestartAvisoObraForm
    } = useRestartAvisoObraForm(selectedObra);

    const {
        formData: deregisterAvisoObraFormData,
        fieldsConfig: deregisterAvisoObraFieldsConfig,
        handleChange: deregisterAvisoObraHandleChange,
        errors: deregisterAvisoObraErrors,
        validateForm: validateDeregisterAvisoObraForm,
        resetForm: resetDeregisterAvisoObraForm
    } = useDeregisterAvisoObraForm();

    const {
        formData: uploadProgramaFormData,
        handleChange: uploadProgramaHandleChange,
        validateForm: validateUploadProgramaForm
    } = useUploadProgramaForm();

    const getTipoObra = () => {
        const tipoIds: number[] =
            selectedObra?.avisosObraTiposSubtipos?.map((t: any) => t.idTipoObra) ?? [];

        const nombres: string[] = tipoIds.map((id) => {
            const tipo = tiposObraData?.find((t) => t.id === id);
            return tipo?.nombre ?? `#${id}`;
        });

        return nombres.length ? nombres.join(', ') : '-';
    };
    const AvisoObraInfo = () => (
        <DrawerActionInfoPaper
            sx={{
                mt: 2,
                mb: 6,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            <DrawerActionInfoPaperItem
                title="Dirección:"
                description={`${selectedObra?.direccion?.calle || ''} ${
                    selectedObra?.direccion?.altura || ''
                }\n${selectedObra?.direccion?.localidad?.nombre || ''} - ${
                    selectedObra?.direccion?.localidad?.provincia?.nombre || ''
                }`}
            />
            <DrawerActionInfoPaperItem title="Tipo de obra:" description={getTipoObra()} />
            <DrawerActionInfoPaperItem
                title="Inicio:"
                description={getDate(selectedObra?.fechaInicio)}
            />
            <DrawerActionInfoPaperItem title="Fin:" description={getDate(selectedObra?.fechaFin)} />
        </DrawerActionInfoPaper>
    );

    const handleSuspendAvisoObra = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal', { variant: 'error' });
            return;
        }
        try {
            if (
                !validateSuspendAvisoObraForm(
                    suspendAvisoObraFormData,
                    suspendAvisoObraFieldsConfig
                )
            ) {
                return;
            }

            const body = {
                fechaSuspencion: new Date(suspendAvisoObraFormData?.suspensionDate),
                motivo: suspendAvisoObraFormData?.motive
            };

            const response: any = await suspendAvisoObra({
                avisoDeObraID: selectedObra?.id,
                body: body
            });
            if (response?.error) {
                handleErrors(response?.error);
                return;
            }
            enqueueSnackbar(t('avisoObraForms.enqueues.suspend.success'), {
                variant: 'success'
            });
            handleCloseDrawer();
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo suspender el aviso de obra',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleExtendAvisoObra = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal...', { variant: 'error' });
            return;
        }
        try {
            if (
                !validateExtendAvisoObraForm(extendAvisoObraFormData, extendAvisoObraFieldsConfig)
            ) {
                return;
            }

            const body = {
                fechaExtension: new Date(extendAvisoObraFormData?.extensionDate),
                observacion: extendAvisoObraFormData?.observation
            };

            const response: any = await extendAvisoObra({
                avisoDeObraID: selectedObra?.id,
                body: body
            });
            if (response?.error) {
                handleErrors(response?.error);
                return;
            }
            enqueueSnackbar(t('avisoObraForms.enqueues.extend.success'), {
                variant: 'success'
            });
            handleCloseDrawer();
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo extender el aviso de obra',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleUploadPrograma = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal', { variant: 'error' });
            return;
        }

        if (!validateUploadProgramaForm()) {
            enqueueSnackbar('Debe adjuntar un archivo PDF.', { variant: 'warning' });
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append(
            'data',
            JSON.stringify({
                adjuntoProgSeguridad: true,
                nombreAdjuntoProgSeguridad: uploadProgramaFormData?.pdfPrograma?.name || '',
                fechaPresentacionPrograma: dayjs().toISOString(),
                estado: 2
            })
        );
        formDataToSend.append('file', uploadProgramaFormData.pdfPrograma as File);

        try {
            const response: any = await updateAvisoObra({
                idAvisoObra: selectedObra.id,
                body: formDataToSend
            });
            if (response?.error) {
                handleErrors(response?.error);
                return;
            }

            enqueueSnackbar('Programa de Seguridad actualizado.', { variant: 'success' });
            handleCloseDrawer();
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo subir el programa de seguridad',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleRestartAvisoObra = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal', { variant: 'error' });
            return;
        }
        try {
            if (
                !validateRestartAvisoObraForm(
                    restartAvisoObraFormData,
                    restartAvisoObraFieldsConfig
                )
            ) {
                return;
            }

            const body = {
                fechaReinicio: new Date(restartAvisoObraFormData?.restartDate),
                fechaFin: restartAvisoObraFormData?.endDate
            };

            const response: any = await restartAvisoObra({
                avisoDeObraID: selectedObra?.id,
                body: body
            });
            if (response?.error) {
                handleErrors(response?.error);
                return;
            }
            enqueueSnackbar(t('avisoObraForms.enqueues.restart.success'), {
                variant: 'success'
            });
            handleCloseDrawer();
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo reactivar el aviso de obra',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleDeregisterAvisoObra = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal', { variant: 'error' });
            return;
        }
        try {
            if (
                !validateDeregisterAvisoObraForm(
                    deregisterAvisoObraFormData,
                    deregisterAvisoObraFieldsConfig
                )
            ) {
                return;
            }

            const body = {
                observacion: deregisterAvisoObraFormData.observation
            };

            const response: any = await deregisterAvisoObra({
                avisoDeObraID: selectedObra?.id,
                body: body
            });
            if (response?.error) {
                handleErrors(response?.error);
                return;
            }
            enqueueSnackbar(t('avisoObraForms.enqueues.deregister.success'), {
                variant: 'success'
            });
            handleCloseDrawer();
            if (onSuccess) onSuccess();
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo dar de baja el aviso de obra',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleConfirmDeregisterAvisoObra = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal', { variant: 'error' });
            return;
        }

        try {
            const motivoBaja = (
                document.querySelector('textarea[name="motivoBaja"]') as HTMLTextAreaElement
            )?.value;

            if (!motivoBaja) {
                enqueueSnackbar('Debe ingresar un motivo de baja.', { variant: 'warning' });
                return;
            }

            const body = {
                observacion: motivoBaja
            };

            const response: any = await deregisterAvisoObra({
                avisoDeObraID: selectedObra?.id,
                body
            });

            if (response?.error) {
                handleErrors(response?.error);
                return;
            }

            enqueueSnackbar(t('avisoObraForms.enqueues.confirmDeregister.success'), {
                variant: 'success'
            });
            handleCloseDrawer();
            if (onSuccess) onSuccess();
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo confirmar la baja del aviso de obra',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleApprovePrograma = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal', { variant: 'error' });
            return;
        }

        try {
            const observaciones = (
                document.querySelector(
                    'textarea[name="observacionAprobacion"]'
                ) as HTMLTextAreaElement
            )?.value;

            const response: any = await generateConstanciaPdf({
                idAvisoObra: selectedObra.id,
                body: {
                    aprobar: true,
                    observaciones
                }
            });

            if (response?.error) {
                handleErrors(response.error);
                handleCloseDrawer();
                return;
            }

            if (response?.data) {
                const base64String = response.data.data;
                downloadFromBase64(base64String, selectedObra.id, 'constancia-aprobacion');
            }

            enqueueSnackbar('Programa aprobado con éxito', { variant: 'success' });
            handleCloseDrawer();
            if (onSuccess) onSuccess();
        } catch (error) {
            handleCloseDrawer();
            if (onSuccess) onSuccess();
            dispatch(
                showAlert({
                    title: 'No se pudo aprobar el programa de seguridad',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleReceivePrograma = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal', { variant: 'error' });
            return;
        }

        try {
            const response: any = await generateConstanciaPdf({
                idAvisoObra: selectedObra.id
            });

            if (response?.error) {
                handleErrors(response.error);
                handleCloseDrawer();
                return;
            }

            if (response?.data) {
                const base64String = response.data.data;
                downloadFromBase64(base64String, selectedObra.id, 'constancia-recepcion');
            }

            enqueueSnackbar('Constancia de recepción generada con éxito', { variant: 'success' });
            handleCloseDrawer();
            if (onSuccess) onSuccess();
        } catch (error) {
            handleCloseDrawer();
            if (onSuccess) onSuccess();
            dispatch(
                showAlert({
                    title: 'No se pudo generar la constancia de recepción',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleRejectPrograma = async () => {
        if (!selectedObra?.id) {
            enqueueSnackbar('Algo salió mal', { variant: 'error' });
            return;
        }

        try {
            const motivoRechazo = (
                document.querySelector(
                    'textarea[name="observacionAprobacion"]'
                ) as HTMLTextAreaElement
            )?.value;

            if (!motivoRechazo) {
                enqueueSnackbar('Debe ingresar un motivo de rechazo.', { variant: 'warning' });
                return;
            }

            const response: any = await generateConstanciaPdf({
                idAvisoObra: selectedObra.id,
                body: {
                    motivoRechazo
                }
            });

            if (response?.error) {
                handleErrors(response.error);
                handleCloseDrawer();
                return;
            }

            enqueueSnackbar('Programa rechazado con éxito', { variant: 'success' });
            handleCloseDrawer();
            if (onSuccess) onSuccess();
        } catch (error) {
            handleCloseDrawer();
            if (onSuccess) onSuccess();
            dispatch(
                showAlert({
                    title: 'No se pudo rechazar el programa de seguridad',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const getDrawerTitle = () => {
        if (actionType === ACTION_TYPES.SUSPEND) {
            return t(`drawer.title.suspend`);
        }
        if (actionType === ACTION_TYPES.EXTEND) {
            return t(`drawer.title.extend`);
        }
        if (actionType === ACTION_TYPES.RESTART) {
            return t(`drawer.title.restart`);
        }
        if (actionType === ACTION_TYPES.DEREGISTER) {
            return t(`drawer.title.deregister`);
        }
        if (actionType === ACTION_TYPES.UPLOAD_PDF_PROGRAMA) {
            return t(`drawer.title.uploadPrograma`);
        }
        if (actionType === ACTION_TYPES.RECEIVE_PROGRAMA) {
            return t('drawer.title.receivePrograma');
        }
        if (actionType === ACTION_TYPES.APPROVE_PROGRAMA) {
            return t('drawer.title.approvePrograma');
        }
        if (actionType === ACTION_TYPES.CONFIRM_DEREGISTER) {
            return t('drawer.title.confirmDeregister');
        }

        return '-';
    };

    const handleConfirm = () => {
        if (actionType === ACTION_TYPES.SUSPEND) {
            handleSuspendAvisoObra();
        }

        if (actionType === ACTION_TYPES.EXTEND) {
            handleExtendAvisoObra();
        }

        if (actionType === ACTION_TYPES.RESTART) {
            handleRestartAvisoObra();
        }

        if (actionType === ACTION_TYPES.DEREGISTER) {
            handleDeregisterAvisoObra();
        }
        if (actionType === ACTION_TYPES.UPLOAD_PDF_PROGRAMA) {
            handleUploadPrograma();
        }
        if (actionType === ACTION_TYPES.CONFIRM_DEREGISTER) {
            handleConfirmDeregisterAvisoObra();
        }
        if (actionType === ACTION_TYPES.RECEIVE_PROGRAMA) {
            handleReceivePrograma();
        }
    };

    const handleCloseDrawer = () => {
        resetSuspendAvisoObraForm();
        resetRestartAvisoObraForm();
        resetExtendAvisoObraForm();
        resetDeregisterAvisoObraForm();
        setOpen(false);
    };

    const canUploadPrograma =
        !selectedObra?.programaSeguridadPresentado ||
        selectedObra?.idEstadosAvisoObra === EstadoAvisoObraEnum.A_REVISAR;

    const renderFields = () => {
        if (actionType === ACTION_TYPES.SUSPEND) {
            return (
                <>
                    <AvisoObraInfo />
                    <Grid mt={2} container spacing={2} rowSpacing={0.01}>
                        {suspendAvisoObraFieldsConfig.map((field, index) => (
                            <Grid
                                item
                                xs={field?.gridSpace?.xs}
                                sm={field?.gridSpace?.sm}
                                md={field?.gridSpace?.md}
                                lg={field?.gridSpace?.lg}
                                key={index}
                            >
                                <CustomField
                                    config={field}
                                    value={
                                        suspendAvisoObraFormData[
                                            field.name as keyof SuspendAvisoObraFormData
                                        ]
                                    }
                                    onChange={suspendAvisoObraHandleChange}
                                    error={suspendAvisoObraErrors[field.name as string]?.hasError}
                                    helperText={
                                        suspendAvisoObraErrors[field.name as string]
                                            ?.errorMessage || field.helperText
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            );
        }

        if (actionType === ACTION_TYPES.EXTEND) {
            const fechaFinOriginal = selectedObra?.fechaFin ? dayjs(selectedObra.fechaFin) : null;

            return (
                <>
                    <AvisoObraInfo />
                    <Grid mt={2} container spacing={2} rowSpacing={0.01}>
                        {extendAvisoObraFieldsConfig.map((field, index) => (
                            <Grid
                                item
                                xs={field?.gridSpace?.xs}
                                sm={field?.gridSpace?.sm}
                                md={field?.gridSpace?.md}
                                lg={field?.gridSpace?.lg}
                                key={index}
                            >
                                <CustomField
                                    config={{
                                        ...field
                                    }}
                                    value={
                                        extendAvisoObraFormData[
                                            field.name as keyof ExtendAvisoObraFormData
                                        ]
                                    }
                                    onChange={extendAvisoObraHandleChange}
                                    error={extendAvisoObraErrors[field.name as string]?.hasError}
                                    helperText={
                                        extendAvisoObraErrors[field.name as string]?.errorMessage ||
                                        field.helperText
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            );
        }

        if (actionType === ACTION_TYPES.RESTART) {
            return (
                <>
                    <AvisoObraInfo />
                    <Grid mt={2} container spacing={2} rowSpacing={0.01}>
                        {restartAvisoObraFieldsConfig.map((field, index) => (
                            <Grid
                                item
                                xs={field?.gridSpace?.xs}
                                sm={field?.gridSpace?.sm}
                                md={field?.gridSpace?.md}
                                lg={field?.gridSpace?.lg}
                                key={index}
                            >
                                <CustomField
                                    config={field}
                                    value={
                                        restartAvisoObraFormData[
                                            field.name as keyof RestartAvisoObraFormData
                                        ]
                                    }
                                    onChange={restartAvisoObraHandleChange}
                                    error={restartAvisoObraErrors[field.name]?.hasError}
                                    helperText={
                                        restartAvisoObraErrors[field.name]?.hasError
                                            ? restartAvisoObraErrors[field.name]?.errorMessage
                                            : field.helperText
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            );
        }

        if (actionType === ACTION_TYPES.DEREGISTER) {
            return (
                <>
                    <AvisoObraInfo />
                    <Grid mt={2} container spacing={2} rowSpacing={0.01}>
                        {deregisterAvisoObraFieldsConfig.map((field, index) => (
                            <Grid
                                item
                                xs={field?.gridSpace?.xs}
                                sm={field?.gridSpace?.sm}
                                md={field?.gridSpace?.md}
                                lg={field?.gridSpace?.lg}
                                key={index}
                            >
                                <CustomField
                                    config={field}
                                    value={
                                        deregisterAvisoObraFormData[
                                            field.name as keyof DeregisterAvisoObraFormData
                                        ]
                                    }
                                    onChange={deregisterAvisoObraHandleChange}
                                    error={
                                        deregisterAvisoObraErrors[field.name as string]?.hasError
                                    }
                                    helperText={
                                        deregisterAvisoObraErrors[field.name as string]
                                            ?.errorMessage || field.helperText
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </>
            );
        }

        if (actionType === ACTION_TYPES.UPLOAD_PDF_PROGRAMA) {
            return (
                <>
                    <Grid mt={1} container spacing={2}>
                        {canUploadPrograma && (
                            <Grid item xs={12}>
                                <FormControl fullWidth required sx={{ padding: '2px' }}>
                                    <label
                                        htmlFor="pdfPrograma"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            border: '1px solid #ccc',
                                            borderRadius: 4,
                                            padding: '12px 16px',
                                            paddingRight: '5px',
                                            cursor: 'pointer',
                                            width: '100%'
                                        }}
                                    >
                                        <Typography
                                            fontSize={14}
                                            color={
                                                uploadProgramaFormData?.pdfPrograma
                                                    ? 'text.primary'
                                                    : 'text.secondary'
                                            }
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {uploadProgramaFormData?.pdfPrograma?.name ||
                                                'Seleccionar archivo PDF *'}
                                        </Typography>

                                        <IconButton component="div" size="small">
                                            <UploadFileIcon />
                                        </IconButton>
                                    </label>

                                    <input
                                        id="pdfPrograma"
                                        type="file"
                                        name="pdfPrograma"
                                        accept="application/pdf"
                                        hidden
                                        onChange={uploadProgramaHandleChange}
                                    />

                                    <Typography variant="caption" color="text.secondary" mt={1}>
                                        Archivos admitidos: PDF
                                    </Typography>
                                </FormControl>
                            </Grid>
                        )}

                        {selectedObra?.programaSeguridad?.length > 0 &&
                            selectedObra.programaSeguridad.map((archivo: any) => (
                                <Grid item xs={12} key={archivo.idPrograma}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#e6eff2',
                                            padding: 2,
                                            borderRadius: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Box>
                                            <Typography fontWeight="bold">
                                                {archivo.urlArchivo
                                                    ? decodeURIComponent(
                                                          archivo.urlArchivo.split('/').pop()
                                                      )
                                                    : '-'}
                                            </Typography>
                                            <Typography>
                                                {selectedObra?.tipoResolucion?.descripcion ?? '-'}
                                            </Typography>
                                            <Typography>
                                                Fecha de carga:{' '}
                                                {new Date(
                                                    archivo.fechaPresentacion
                                                ).toLocaleDateString('es-AR')}
                                            </Typography>
                                        </Box>
                                        {archivo.urlArchivo && (
                                            <a
                                                href={archivo.urlArchivo}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: 'none' }}
                                            >
                                                <Box>
                                                    <IconButton component="div" size="small">
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Box>
                                            </a>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                    </Grid>
                </>
            );
        }

        if (actionType === ACTION_TYPES.RECEIVE_PROGRAMA) {
            return (
                <>
                    <Grid mt={1} container spacing={2}>
                        {selectedObra?.programaSeguridad?.length > 0 &&
                            selectedObra.programaSeguridad.map((archivo: any) => (
                                <Grid item xs={12} key={archivo.idPrograma}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#e6eff2',
                                            padding: 2,
                                            borderRadius: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Box>
                                            <Typography fontWeight="bold">
                                                {archivo.urlArchivo
                                                    ? decodeURIComponent(
                                                          archivo.urlArchivo.split('/').pop()
                                                      )
                                                    : '-'}
                                            </Typography>
                                            <Typography>
                                                Fecha presentación:{' '}
                                                {new Date(
                                                    archivo.fechaPresentacion
                                                ).toLocaleDateString('es-AR')}
                                            </Typography>
                                        </Box>
                                        {archivo.urlArchivo && (
                                            <a
                                                href={archivo.urlArchivo}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: 'none' }}
                                            >
                                                <IconButton component="div" size="small">
                                                    <DownloadIcon />
                                                </IconButton>
                                            </a>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                    </Grid>
                </>
            );
        }

        if (actionType === ACTION_TYPES.APPROVE_PROGRAMA) {
            return (
                <>
                    <Grid mt={1} container spacing={2}>
                        {selectedObra?.programaSeguridad?.length > 0 &&
                            selectedObra.programaSeguridad.map((archivo: any) => (
                                <Grid item xs={12} key={archivo.idPrograma}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#e6eff2',
                                            padding: 2,
                                            borderRadius: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Box>
                                            <Typography fontWeight="bold">
                                                {archivo.urlArchivo
                                                    ? decodeURIComponent(
                                                          archivo.urlArchivo.split('/').pop()
                                                      )
                                                    : '-'}
                                            </Typography>
                                            <Typography>
                                                Fecha de carga:{' '}
                                                {new Date(
                                                    archivo.fechaPresentacion
                                                ).toLocaleDateString('es-AR')}
                                            </Typography>
                                        </Box>
                                        {archivo.urlArchivo && (
                                            <a
                                                href={archivo.urlArchivo}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ textDecoration: 'none' }}
                                            >
                                                <IconButton component="div" size="small">
                                                    <DownloadIcon />
                                                </IconButton>
                                            </a>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        <Grid item xs={12}>
                            <TextField
                                label="Observaciones"
                                multiline
                                rows={3}
                                fullWidth
                                name="observacionAprobacion"
                            />
                        </Grid>
                    </Grid>
                </>
            );
        }

        if (actionType === ACTION_TYPES.CONFIRM_DEREGISTER) {
            return (
                <Grid mt={2} container spacing={2}>
                    <Grid item xs={12}>
                        <Typography fontWeight="bold" sx={{ mb: 1 }}>
                            Responsable de la solicitud
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <TextField label="Nombre" value={user?.nombre || ''} fullWidth disabled />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <TextField
                            label="Apellido"
                            value={user?.apellido || ''}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <TextField label="Documento" value={user?.cuil || ''} fullWidth disabled />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <TextField label="Email" value={user?.email || ''} fullWidth disabled />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <TextField
                            label="Teléfono"
                            value={user?.telefono || ''}
                            fullWidth
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Motivo de la baja"
                            multiline
                            rows={3}
                            fullWidth
                            name="motivoBaja"
                        />
                    </Grid>
                </Grid>
            );
        }

        return null;
    };

    return (
        <DrawerContainer open={open} setOpen={handleCloseDrawer}>
            <DrawerHeader onIconClick={handleCloseDrawer} text={getDrawerTitle()} />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '80vh'
                }}
            >
                <Box sx={{ flex: 1, px: 3, py: 1, overflowY: 'auto' }}>
                    <Grid container spacing={2}>
                        {renderFields()}
                    </Grid>
                </Box>

                <Box
                    sx={{
                        p: 2,
                        borderTop: '1px solid #eee',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2.5,
                        backgroundColor: 'white'
                    }}
                >
                    {actionType === ACTION_TYPES.APPROVE_PROGRAMA ? (
                        <>
                            <Button variant="outlined" onClick={handleCloseDrawer}>
                                {t('form.cancelar')}
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleRejectPrograma}
                                disabled={isGeneratingConstanciaPdf}
                            >
                                {t('form.reject')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleApprovePrograma}
                                disabled={isGeneratingConstanciaPdf}
                            >
                                {t('form.aprovee')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outlined" onClick={handleCloseDrawer}>
                                {t('form.cancelar')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleConfirm}
                                disabled={
                                    (actionType === ACTION_TYPES.UPLOAD_PDF_PROGRAMA &&
                                        !canUploadPrograma) ||
                                    isUpdatingPrograma
                                }
                            >
                                {actionType === ACTION_TYPES.CONFIRM_DEREGISTER
                                    ? 'Confirmar Baja'
                                    : actionType === ACTION_TYPES.RECEIVE_PROGRAMA
                                    ? 'Generar Constancia de Recepción'
                                    : t('form.guardar')}
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            <BackdropComponent
                loading={
                    isSuspendingAvisoObra ||
                    isExtendingAvisoObra ||
                    isRestartingAvisoObra ||
                    isDeregisteringAvisoObra ||
                    isUpdatingPrograma ||
                    isGeneratingConstanciaPdf
                }
            />
        </DrawerContainer>
    );
};

export default DrawerAvisoObra;
